import { settings } from "app";
import { BooleanField, Datagrid, ReferenceField, TextField } from "react-admin";
import { Box, useMediaQuery } from "@mui/material";
import { sectionWrapperSx } from "./sectionWrapper";
import { administrationRequests } from "api/administrationApi";

import TabsComponent from "./TabsComponent";
import { ATTESTATIONS_TABLE_MIN_WIDTH } from "./semesterGrid";
import GradesDatagrid from "./GradesDatagrid";
import PickedDisciplines from "./PickedDisciplines";
import CreditsSummary from "./CreditsSummary";
import { splitGradesByType } from "./gradesByType";
import { useCallback, useEffect, useMemo, useState } from "react";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import ManageAttestationsButton from "../AttestationsManagement/ManageAttestationsButton";

const EducationData = ({ record }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const defaultTab = record?.course - 1;

  const [attestations, setAttestation] = useState({});
  const [grades, setGrades] = useState({});
  const [credits, setCredits] = useState(null);
  const [attestationTab, setAttestationTab] = useState(0);
  const [gradesTab, setGradesTab] = useState(0);

  // `record` arrives after mount, so the student's current course only becomes known on a later render.
  useEffect(() => {
    setAttestationTab(defaultTab || 0);
    setGradesTab(defaultTab || 0);
  }, [defaultTab]);

  const fetchAttestations = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentEducationAttestations(record?.id),
        null,
        "GET",
      );
      setAttestation(data || {});
    } catch (err) {
      console.error(err);
    }
  }, [record?.id]);
  const fetchGrades = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentEducationGrades(record?.id),
        null,
        "GET",
      );
      setGrades(data || {});
    } catch (err) {
      console.error(err);
    }
  }, [record?.id]);

  const fetchCredits = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentEducationCredits(record?.id),
        null,
        "GET",
      );
      setCredits(data || null);
    } catch (err) {
      console.error(err);
    }
  }, [record?.id]);

  useEffect(() => {
    if (!record?.id) return;
    Promise.all([fetchAttestations(), fetchGrades(), fetchCredits()]);
  }, [record?.id, fetchAttestations, fetchGrades, fetchCredits]);

  const buildTabsAndData = (rawData, totalCourses) => {
    const semesterKeys = Object.keys(rawData).filter(k => k !== "-1");

    const noSemestersItems = rawData["-1"] || [];

    const byCourse = {};
    noSemestersItems.forEach(item => {
      const key = `extra_${item.course}`;
      if (!byCourse[key]) byCourse[key] = [];
      byCourse[key].push(item);
    });

    const enrichedData = { ...rawData, ...byCourse };

    const tabs = [];
    for (let course = 1; course <= totalCourses; course++) {
      const sem1 = String(course * 2 - 1);
      const sem2 = String(course * 2);

      if (semesterKeys.includes(sem1) || semesterKeys.includes(sem2)) {
        tabs.push([sem1, sem2].filter(k => semesterKeys.includes(k)));
      } else {
        const extraKey = `extra_${course}`;
        if (!enrichedData[extraKey]) enrichedData[extraKey] = [];
        tabs.push([extraKey]);
      }
    }

    return { tabs, data: enrichedData };
  };

  const totalCourses = Math.ceil(record?.curriculumYearsOfStudy ?? 1);
  const { tabs: coursesAttestationTabs, data: attestationsData } = buildTabsAndData(attestations, totalCourses);
  const { mainBySemester, electiveTableRows, facultativeTableRows } = useMemo(
    () => splitGradesByType(grades),
    [grades],
  );
  const { tabs: coursesGradesTabs, data: gradesData } = buildTabsAndData(mainBySemester, totalCourses);
  const selectedCourse = gradesTab + 1;
  const courseElectiveRows = electiveTableRows.filter(row => row.course === selectedCourse);
  const courseFacultativeRows = facultativeTableRows.filter(row => row.course === selectedCourse);

  return (
    <Box sx={sectionWrapperSx}>
      <TabsComponent
        title={"Заверки"}
        isMobile={isMobile}
        array={coursesAttestationTabs}
        responseObject={attestationsData}
        value={attestationTab}
        onChange={setAttestationTab}
        headerAction={<ManageAttestationsButton record={record} />}
        // 3-column table - stays side by side on narrower screens than the grades one.
        minColumnWidth={ATTESTATIONS_TABLE_MIN_WIDTH}
      >
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          sx={settings.style.secondaryDatagrid}
        >
          <ReferenceField
            label="Дисциплина"
            source="disciplineId"
            reference={administrationRequests.getDisciplinesSelect()}
            sortable={false}
          >
            <TextField
              source="name"
              label="Дисциплина"
            />
          </ReferenceField>
          <ReferenceField
            source="classTypeId"
            reference={administrationRequests.getRoomClassTypesSelect()}
            label="Тип занятие"
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>

          <BooleanField
            source="signedOff"
            label="Заверка"
          />
        </Datagrid>
      </TabsComponent>
      <TabsComponent
        title={"Оценки"}
        isMobile={isMobile}
        array={coursesGradesTabs}
        responseObject={gradesData}
        value={gradesTab}
        onChange={setGradesTab}
      >
        <GradesDatagrid
          educationId={record?.id}
          onGradeChanged={fetchGrades}
        />
      </TabsComponent>

      <PickedDisciplines
        electiveRows={courseElectiveRows}
        facultativeRows={courseFacultativeRows}
        educationId={record?.id}
        onGradeChanged={fetchGrades}
        isMobile={isMobile}
      />

      <CreditsSummary
        credits={credits}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default EducationData;
